import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import { TrendingUp, Heart, MessageCircle, Eye, Users, FileText, Share2, Loader2, Calendar, Trophy } from 'lucide-react';
import { dashboardAPI } from '../services/api';

const EngagementAnalytics = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchAnalytics();
    }
  }, [isOpen, period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const response = await dashboardAPI.getEngagementAnalytics(period);
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch engagement analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const periodOptions = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  const MetricCard = ({ icon: Icon, label, value, subtext, color }) => (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${color}20` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-2xl font-bold text-foreground">{value}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
          {subtext && <p className="text-xs text-muted-foreground mt-0.5">{subtext}</p>}
        </div>
      </div>
    </div>
  );

  const metrics = analyticsData?.metrics || {};
  const timeline = analyticsData?.timeline || [];
  const topPost = analyticsData?.topPost;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <DialogTitle className="flex items-center gap-2 text-xl">
              <TrendingUp className="w-6 h-6 text-primary" />
              Engagement Analytics
            </DialogTitle>
            
            {/* Timeline Selector */}
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
              {periodOptions.map((opt) => (
                <Button
                  key={opt.value}
                  size="sm"
                  variant={period === opt.value ? 'default' : 'ghost'}
                  className={period === opt.value ? 'text-white' : ''}
                  className={period === opt.value ? 'btn-primary' : ''}
                  onClick={() => setPeriod(opt.value)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6 mt-4">
            {/* Engagement Trend Chart */}
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-4 text-foreground flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary" />
                  Engagement Trend ({periodOptions.find(p => p.value === period)?.label})
                </h3>
                <div className="h-64">
                  {timeline.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={timeline}>
                        <defs>
                          <linearGradient id="colorLikes" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8854C8" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#8854C8" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="colorComments" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#B894E0" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#B894E0" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis 
                          dataKey="label" 
                          tick={{ fontSize: 11 }} 
                          interval={period === '30d' ? 4 : 0}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'var(--background)', 
                            border: '1px solid var(--border)',
                            borderRadius: '8px'
                          }}
                          formatter={(value, name) => [value, name.charAt(0).toUpperCase() + name.slice(1)]}
                        />
                        <Legend />
                        <Area 
                          type="monotone" 
                          dataKey="likes" 
                          stroke="#8854C8" 
                          fillOpacity={1} 
                          fill="url(#colorLikes)"
                          name="Likes"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="comments" 
                          stroke="#B894E0" 
                          fillOpacity={1} 
                          fill="url(#colorComments)"
                          name="Comments"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      No data available for this period
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Daily Interactions Bar Chart */}
            <Card>
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-4 text-foreground">Interactions Breakdown</h3>
                <div className="h-48">
                  {timeline.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={timeline}>
                        <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                        <XAxis 
                          dataKey="label" 
                          tick={{ fontSize: 11 }} 
                          interval={period === '30d' ? 4 : 0}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'var(--background)', 
                            border: '1px solid var(--border)',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Bar dataKey="likes" fill="#8854C8" name="Likes" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="comments" fill="#B894E0" name="Comments" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="posts" fill="#6B3FA0" name="Posts" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex items-center justify-center h-full text-muted-foreground">
                      No data available for this period
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Key Metrics Grid */}
            <div>
              <h3 className="text-sm font-semibold mb-4 text-foreground">Key Metrics (Real-time)</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <MetricCard 
                  icon={TrendingUp}
                  label="Engagement Rate"
                  value={`${metrics.engagementRate || 0}%`}
                  subtext="(Interactions / Followers)"
                  color="#8854C8"
                />
                <MetricCard 
                  icon={Heart}
                  label="Total Likes"
                  value={metrics.totalLikes || 0}
                  subtext={`${metrics.avgLikesPerPost || 0} avg/post`}
                  color="#9B6DD4"
                />
                <MetricCard 
                  icon={MessageCircle}
                  label="Total Comments"
                  value={metrics.totalComments || 0}
                  subtext={`${metrics.avgCommentsPerPost || 0} avg/post`}
                  color="#B894E0"
                />
                <MetricCard 
                  icon={FileText}
                  label="Total Posts"
                  value={metrics.totalPosts || 0}
                  color="#6B3FA0"
                />
                <MetricCard 
                  icon={Users}
                  label="Followers"
                  value={metrics.followers || 0}
                  color="#7545B5"
                />
                <MetricCard 
                  icon={Users}
                  label="Following"
                  value={metrics.following || 0}
                  color="#A47DDB"
                />
                <MetricCard 
                  icon={Eye}
                  label="Profile Views"
                  value={metrics.profileViews || 0}
                  color="#8854C8"
                />
                <MetricCard 
                  icon={Share2}
                  label="Total Interactions"
                  value={metrics.totalInteractions || 0}
                  subtext={`${metrics.avgInteractionsPerPost || 0} per post`}
                  color="#C4A0E8"
                />
              </div>
            </div>

            {/* Top Performing Post */}
            {topPost && (
              <Card className="bg-gradient-to-r from-purple-50 to-purple-100/50 dark:from-purple-900/20 dark:to-purple-800/10 border-purple-200 dark:border-purple-800">
                <CardContent className="p-4">
                  <h3 className="text-sm font-semibold mb-3 text-foreground flex items-center gap-2">
                    <Trophy className="w-4 h-4" style={{ color: '#8854C8' }} />
                    Top Performing Post
                  </h3>
                  <div className="flex items-start gap-4">
                    <div className="flex-1">
                      <p className="text-sm text-foreground line-clamp-2">{topPost.content}...</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3 text-primary" /> {topPost.likes} likes
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3 text-purple-400" /> {topPost.comments} comments
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Engagement Formula Explanation */}
            <Card className="bg-primary/10 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold mb-2 text-foreground flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  How Engagement is Calculated
                </h3>
                <div className="text-sm text-muted-foreground space-y-2">
                  <p>
                    <strong>Engagement Rate:</strong> (Total Likes + Total Comments) ÷ Followers × 100
                  </p>
                  <p>
                    <strong>Your calculation:</strong> ({metrics.totalLikes || 0} + {metrics.totalComments || 0}) ÷ {metrics.followers || 1} × 100 = <span className="font-bold text-foreground">{metrics.engagementRate || 0}%</span>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EngagementAnalytics;
